import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const AIRCRAFT_TYPES = [
  'A320 Family',
  'A330',
  'A350',
  'B737',
  'B757/767',
  'B777',
  'B787',
  'CRJ',
  'E170/175',
  'E190/195',
];

const POSITIONS = [
  'Captain',
  'First Officer',
  'Relief Pilot',
  'Check Airman',
  'Line Check Airman',
];

const HOME_BASES = [
  'ATL', 'BOS', 'CLT', 'DEN', 'DFW', 'DTW', 'EWR', 'IAD', 'IAH', 'JFK',
  'LAX', 'LGA', 'MCO', 'MIA', 'MSP', 'ORD', 'PHL', 'PHX', 'SEA', 'SFO',
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    employee_id: '',
    employee_name: '',
    position: '',
    date_of_hire: '',
    home_base: '',
    aircraft_type_group: '',
  });

  // If already has employee_id, redirect to dashboard
  useEffect(() => {
    if (userProfile?.employee_id) {
      navigate('/dashboard');
    }
  }, [userProfile, navigate]);

  // If not logged in, redirect to login
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.employee_id?.trim()) {
      setError('Employee ID is required');
      return false;
    }
    if (!formData.employee_name?.trim()) {
      setError('Full name is required');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.position) {
      setError('Position is required');
      return false;
    }
    if (!formData.home_base) {
      setError('Home base is required');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(1, prev - 1));
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Check if we're in dev mode
      const devModeEnabled = localStorage.getItem('winglet_dev_mode') === 'true';
      
      if (devModeEnabled) {
        // Dev mode - just set the flag and redirect
        localStorage.setItem('winglet_dev_onboarding_complete', 'true');
        window.location.href = '/dashboard';
        return;
      }

      // Normalize employee_id (remove leading zeros for consistency)
      const normalizedEmployeeId = formData.employee_id?.replace(/^0+/, '') || formData.employee_id;

      // 1. Update user_profiles with employee_id (id IS the auth UUID already)
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          employee_id: normalizedEmployeeId,
          employee_name: formData.employee_name,
          full_name: formData.employee_name,
        })
        .eq('id', user?.id);

      if (profileError) throw profileError;

      // 2. Create or update user_settings (linked to auth user)
      const now = new Date().toISOString();
      const { error: settingsError } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user?.id,  // Link to auth.users
          employee_id: normalizedEmployeeId,
          employee_name: formData.employee_name,  // Required
          position: formData.position,
          date_of_hire: formData.date_of_hire,
          home_base: formData.home_base,
          aircraft_type_group: formData.aircraft_type_group || 'NARROWBODY',
          aircraft_category: 'CA',  // Required field
          default_aircraft_model: 'A321',  // Required field
          preferences: {
            theme: 'dark',
            notifications: true,
          },
          created_at: now,
          updated_at: now,
        }, {
          onConflict: 'employee_id',
        });

      if (settingsError) throw settingsError;

      // Success - reload to pick up new profile
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Onboarding error:', err);
      setError(err?.message || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
          <Icon name="User" size={32} color="var(--color-accent-cyan)" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Your Identity</h2>
        <p className="text-muted-foreground mt-2">Let's start with your basic information</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Employee ID <span className="text-error">*</span>
          </label>
          <Input
            type="text"
            placeholder="e.g., 141986 or 0141986"
            value={formData.employee_id}
            onChange={(e) => handleChange('employee_id', e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Your airline employee number (found on your ID badge)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Full Name <span className="text-error">*</span>
          </label>
          <Input
            type="text"
            placeholder="LAST, FIRST MIDDLE"
            value={formData.employee_name}
            onChange={(e) => handleChange('employee_name', e.target.value?.toUpperCase())}
          />
          <p className="text-xs text-muted-foreground mt-1">
            As it appears on your rotations (e.g., SMITH, JOHN DAVID)
          </p>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
          <Icon name="Plane" size={32} color="var(--color-accent-purple)" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Your Position</h2>
        <p className="text-muted-foreground mt-2">Tell us about your role</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Position <span className="text-error">*</span>
          </label>
          <select
            value={formData.position}
            onChange={(e) => handleChange('position', e.target.value)}
            className="w-full bg-card border border-border rounded-lg px-4 py-3 text-foreground focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          >
            <option value="">Select your position...</option>
            {POSITIONS.map(pos => (
              <option key={pos} value={pos}>{pos}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Home Base <span className="text-error">*</span>
          </label>
          <select
            value={formData.home_base}
            onChange={(e) => handleChange('home_base', e.target.value)}
            className="w-full bg-card border border-border rounded-lg px-4 py-3 text-foreground focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          >
            <option value="">Select your home base...</option>
            {HOME_BASES.map(base => (
              <option key={base} value={base}>{base}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Date of Hire
          </label>
          <Input
            type="date"
            value={formData.date_of_hire}
            onChange={(e) => handleChange('date_of_hire', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Almost Done!</h2>
        <p className="text-muted-foreground mt-2">Select your aircraft type (optional)</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Aircraft Type
          </label>
          <select
            value={formData.aircraft_type_group}
            onChange={(e) => handleChange('aircraft_type_group', e.target.value)}
            className="w-full bg-card border border-border rounded-lg px-4 py-3 text-foreground focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          >
            <option value="">Select aircraft type...</option>
            {AIRCRAFT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-8 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
        <h3 className="font-semibold text-foreground mb-2">Profile Summary</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <span className="text-muted-foreground">Employee ID:</span>
          <span className="text-foreground">{formData.employee_id}</span>
          <span className="text-muted-foreground">Name:</span>
          <span className="text-foreground">{formData.employee_name}</span>
          <span className="text-muted-foreground">Position:</span>
          <span className="text-foreground">{formData.position}</span>
          <span className="text-muted-foreground">Home Base:</span>
          <span className="text-foreground">{formData.home_base}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg">
        <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  s <= step 
                    ? 'bg-cyan-500 text-white' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {s < step ? <Icon name="Check" size={20} /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 rounded ${
                    s < step ? 'bg-cyan-500' : 'bg-muted'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/30 rounded-lg">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Step Content */}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className="flex gap-4 mt-8">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={loading}
                className="flex-1"
              >
                Back
              </Button>
            )}
            
            {step < 3 ? (
              <Button
                onClick={handleNext}
                className="flex-1"
              >
                Continue
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                loading={loading}
                disabled={loading}
                className="flex-1"
              >
                Complete Setup
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          You can update these settings anytime in Settings → Profile
        </p>
      </div>
    </div>
  );
};

export default Onboarding;
